/**
 * Helper utilities for human-friendly stage directions and formation math in Story Engine.
 * Stage dimensions: 1920x1080 with origin (0,0) at CENTER.
 * X-axis: -960 (left) to +960 (right)
 * Y-axis: -540 (top) to +540 (bottom)
 */

export interface Position2D {
  x: number;
  y: number;
}

export class StoryHelpers {
  /**
   * Resolves human-friendly direction strings to (x, y) stage coordinates.
   */
  public static resolveStagePosition(pos: string | number | undefined, defaultX = 0, defaultY = 250): Position2D {
    if (typeof pos === 'number') {
      return { x: pos, y: defaultY };
    }
    if (!pos || typeof pos !== 'string') {
      return { x: defaultX, y: defaultY };
    }

    const key = pos.toLowerCase().trim();
    switch (key) {
      case 'far_left':
      case 'offscreen_left':
        return { x: -850, y: defaultY };
      case 'left':
        return { x: -550, y: defaultY };
      case 'center_left':
        return { x: -250, y: defaultY };
      case 'center':
        return { x: 0, y: defaultY };
      case 'center_right':
        return { x: 250, y: defaultY };
      case 'right':
        return { x: 550, y: defaultY };
      case 'far_right':
      case 'offscreen_right':
        return { x: 850, y: defaultY };
      case 'top_left':
        return { x: -550, y: -300 };
      case 'top':
        return { x: 0, y: -300 };
      case 'top_right':
        return { x: 550, y: -300 };
      case 'bottom_left':
        return { x: -550, y: 350 };
      case 'bottom':
        return { x: 0, y: 350 };
      case 'bottom_right':
        return { x: 550, y: 350 };
      default:
        return { x: defaultX, y: defaultY };
    }
  }

  /**
   * Calculates target position when moving in a human-friendly direction.
   */
  public static calculateTargetPosition(
    startPos: Position2D,
    direction: string,
    distanceParam?: string | number
  ): Position2D {
    let distance = 400;
    if (typeof distanceParam === 'number') {
      distance = distanceParam;
    } else if (distanceParam === 'short') {
      distance = 200;
    } else if (distanceParam === 'far') {
      distance = 700;
    }

    const dirKey = (direction || 'right').toLowerCase().trim();
    switch (dirKey) {
      case 'left':
      case 'backward':
        return { x: Math.max(-900, startPos.x - distance), y: startPos.y };
      case 'right':
      case 'forward':
        return { x: Math.min(900, startPos.x + distance), y: startPos.y };
      case 'up':
        return { x: startPos.x, y: Math.max(-480, startPos.y - distance) };
      case 'down':
        return { x: startPos.x, y: Math.min(480, startPos.y + distance) };
      default:
        return { x: Math.min(900, startPos.x + distance), y: startPos.y };
    }
  }

  /**
   * Computes formation offsets for follower group actions.
   */
  public static computeFormationPositions(
    leaderPos: Position2D,
    followerCount: number,
    formation: string = 'flank'
  ): Position2D[] {
    const results: Position2D[] = [];
    const spacing = 120;

    for (let i = 0; i < followerCount; i++) {
      const idx = i + 1;
      if (formation === 'flank') {
        const side = idx % 2 === 1 ? -1 : 1;
        const row = Math.ceil(idx / 2);
        results.push({
          x: leaderPos.x - row * spacing,
          y: leaderPos.y + side * 40
        });
      } else if (formation === 'line' || formation === 'behind') {
        results.push({
          x: leaderPos.x - idx * spacing,
          y: leaderPos.y
        });
      } else if (formation === 'wedge') {
        const side = idx % 2 === 1 ? -1 : 1;
        const row = Math.ceil(idx / 2);
        results.push({
          x: leaderPos.x - row * spacing,
          y: leaderPos.y + side * row * 50
        });
      } else {
        results.push({
          x: leaderPos.x - idx * spacing,
          y: leaderPos.y
        });
      }
    }
    return results;
  }
}
